import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { createColumnHelper, type ColumnDef } from '@tanstack/react-table'
import { BellRing, Plus } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { billsQueryOptions } from '@/features/bills/api'
import { chargeKeys, createCharge } from '@/features/billing/charges'
import { CobrancaBulkBar } from '@/features/billing/cobranca-bulk-bar'
import {
  COBRANCAS_FILTROS,
  COBRANCAS_ORDEM_DEFAULT,
  cobrancasSearchSchema,
  toCobrancaBillParams,
  type CobrancasSearch,
} from '@/features/billing/search'
import { schedulesQueryOptions } from '@/features/schedules/api/queries'
import { formatBusinessDate } from '@/features/schedules/components/date-text'
import { MoneyText } from '@/features/schedules/components/money-text'
import type { BillOut } from '@/shared/api/types'
import { useIdempotencyKey } from '@/shared/api/use-idempotency-key'
import { useApiErrorMessage } from '@/shared/i18n/use-api-error-message'
import { Badge, type BadgeProps } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import { Drawer } from '@/shared/ui/drawer'
import { EmptyState } from '@/shared/ui/empty-state'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { SelectNative } from '@/shared/ui/select-native'
import { PageHeader } from '@/widgets/app-shell/page-header'
import { DataTable } from '@/widgets/data-table/data-table'
import { NoResultsState } from '@/widgets/data-table/no-results-state'
import { TablePager } from '@/widgets/data-table/table-pager'
import { fromSortingState, toSortingState } from '@/widgets/data-table/table-search'
import { FiltroCampo, TableToolbar, type FiltroChip } from '@/widgets/data-table/table-toolbar'

export const Route = createFileRoute('/_authed/recebimentos/central-de-cobranca')({
  validateSearch: cobrancasSearchSchema,
  loaderDeps: ({ search }) => ({ params: toCobrancaBillParams(search) }),
  loader: ({ context, deps }) =>
    context.queryClient.ensureQueryData(billsQueryOptions(deps.params)),
  component: CobrancaPage,
})

const col = createColumnHelper<BillOut>()

/** Tom do badge por status do recebível (mesmo mapa da tabela de bills). */
const BILL_STATUS_TONE: Record<string, BadgeProps['tone']> = {
  open: 'info',
  settled: 'positive',
  overdue: 'danger',
  partial: 'caution',
  unknown: 'neutral',
}
const KNOWN_STATUS = new Set(Object.keys(BILL_STATUS_TONE))

function CobrancaPage() {
  const { t, i18n } = useTranslation(['billing', 'bills', 'common'])
  const [novaAberta, setNovaAberta] = useState(false)

  const search = Route.useSearch()
  const navigate = Route.useNavigate()

  // A Central de cobrança LISTA os RECEBÍVEIS (contas a receber das duas
  // origens — contábil e banco/Hatch), como na Nibo: dá visibilidade do que há
  // para cobrar. Usa a lista COMPOSTA /v1/bills.
  const { data, isLoading, isFetching, error, refetch } = useQuery(
    billsQueryOptions(toCobrancaBillParams(search)),
  )

  // Seleção para a baixa em lote: só a linha CONTÁBIL EM ABERTO é baixável (a do
  // banco não tem endpoint de baixa), como na tela de Receber.
  const [selecionados, setSelecionados] = useState<ReadonlySet<string>>(() => new Set())
  const selecionaveis = useMemo(
    () => (data?.items ?? []).filter((b) => b.source === 'schedule' && b.status === 'open'),
    [data],
  )
  const selecionadosBills = useMemo(
    () => selecionaveis.filter((b) => selecionados.has(b.id)),
    [selecionaveis, selecionados],
  )
  // Trocar de página/filtro/ordenação limpa a seleção — um id de outra página
  // não aparece aqui, e baixar "o que sobrou" mentiria. Ajuste no render (padrão
  // "You Might Not Need an Effect"), como na tela de Receber.
  const chaveSel = JSON.stringify(search)
  const [chaveSelAnterior, setChaveSelAnterior] = useState(chaveSel)
  if (chaveSel !== chaveSelAnterior) {
    setChaveSelAnterior(chaveSel)
    setSelecionados(new Set())
  }

  const aplicar = (patch: Partial<CobrancasSearch>) => {
    // replace: filtrar não é navegação — empilhar cada tecla da busca faria o
    // "voltar" do navegador desfazer letra por letra.
    void navigate({ search: (prev: CobrancasSearch) => ({ ...prev, ...patch }), replace: true })
  }
  /** Filtrar/ordenar volta para a página 1 — senão abre num offset vazio. */
  const filtrar = (patch: Partial<CobrancasSearch>) => {
    aplicar({ ...patch, pagina: 1 })
  }
  const limparFiltros = () => {
    filtrar(Object.fromEntries(COBRANCAS_FILTROS.map((k) => [k, undefined])))
  }

  const chips: FiltroChip[] = []
  if (search.q !== undefined) {
    chips.push({
      id: 'q',
      label: `${t('billing:filters.description')}: ${search.q}`,
      onRemove: () => {
        filtrar({ q: undefined })
      },
    })
  }
  if (search.de !== undefined || search.ate !== undefined) {
    chips.push({
      id: 'due',
      label: `${t('billing:filters.dueRange')}: ${search.de ?? '…'} – ${search.ate ?? '…'}`,
      onRemove: () => {
        filtrar({ de: undefined, ate: undefined })
      },
    })
  }
  if (search.status !== undefined) {
    chips.push({
      id: 'status',
      label: `${t('billing:filters.status')}: ${t(`billing:scheduleStatus.${search.status}` as 'billing:scheduleStatus.open')}`,
      onRemove: () => {
        filtrar({ status: undefined })
      },
    })
  }

  const columns = useMemo(() => {
    const selCount = selecionaveis.filter((c) => selecionados.has(c.id)).length
    const selectCol = col.display({
      id: 'select',
      header: () => (
        <SelectAllCheckbox
          checked={selCount > 0 && selCount === selecionaveis.length}
          indeterminate={selCount > 0 && selCount < selecionaveis.length}
          disabled={selecionaveis.length === 0}
          aria-label={t('billing:bulk.selectAllAria')}
          onChange={() => {
            setSelecionados((prev) => {
              const todos = selecionaveis.every((c) => prev.has(c.id))
              return todos ? new Set() : new Set(selecionaveis.map((c) => c.id))
            })
          }}
        />
      ),
      cell: (c) => {
        const b = c.row.original
        // Só a linha contábil em aberto é baixável (a do banco não tem baixa).
        if (b.source !== 'schedule' || b.status !== 'open') return null
        return (
          <input
            type="checkbox"
            className="accent-action size-3.5"
            checked={selecionados.has(b.id)}
            aria-label={t('billing:bulk.selectRowAria', {
              name: b.counterparty_name ?? b.description ?? '',
            })}
            onChange={() => {
              setSelecionados((prev) => {
                const next = new Set(prev)
                if (next.has(b.id)) next.delete(b.id)
                else next.add(b.id)
                return next
              })
            }}
          />
        )
      },
    })

    const rest = [
      col.accessor((r) => r.due_date, {
        id: 'due_date',
        header: t('billing:columns.dueDate'),
        cell: (c) => formatBusinessDate(c.getValue(), i18n.language),
        enableSorting: true,
      }),
      col.accessor((r) => r.counterparty_name, {
        id: 'stakeholder',
        header: t('billing:columns.contact'),
        cell: (c) => c.getValue() ?? '—',
        enableSorting: false,
      }),
      col.accessor((r) => r.description, {
        id: 'description',
        header: t('billing:columns.description'),
        cell: (c) => c.getValue() ?? '—',
        enableSorting: false,
      }),
      col.accessor((r) => r.status, {
        id: 'status',
        header: t('billing:columns.status'),
        enableSorting: true,
        cell: (c) => {
          const s = c.getValue()
          return (
            <Badge tone={BILL_STATUS_TONE[s] ?? 'neutral'}>
              {KNOWN_STATUS.has(s) ? t(`bills:status.${s}` as 'bills:status.open') : s}
            </Badge>
          )
        },
      }),
      col.accessor((r) => r.amount, {
        id: 'amount',
        header: t('billing:columns.amount'),
        cell: (c) => <MoneyText value={c.getValue()} tone="income" />,
        enableSorting: true,
      }),
    ]

    return [selectCol, ...rest] as ColumnDef<BillOut>[]
  }, [t, i18n.language, selecionados, selecionaveis])

  const acoes = (
    <div className="flex items-center gap-2">
      {/* Sem back-end de "nota de débito" (não é o mesmo que cobrança/NFS-e). */}
      <Button variant="secondary" disabled title={t('common:comingSoon.body')}>
        {t('billing:actions.newDebitNote')}
      </Button>
      {/* "Fatura" = registrar a cobrança de um recebimento em aberto (o que o
          back-end realmente suporta: POST /v1/charges). */}
      <Button
        variant="primary"
        onClick={() => {
          setNovaAberta(true)
        }}
      >
        <Plus aria-hidden />
        {t('billing:actions.newInvoice')}
      </Button>
    </div>
  )

  return (
    <>
      <PageHeader title={t('billing:pageTitle')} actions={acoes} />
      <div className="flex flex-col gap-4 p-6">
        <TableToolbar
          inline
          busca={search.q}
          onBuscaChange={(q) => {
            filtrar({ q })
          }}
          buscaPlaceholder={t('billing:filters.searchPlaceholder')}
          chips={chips}
          onLimpar={limparFiltros}
        >
          <FiltroCampo id="filtro-de" label={t('billing:filters.from')}>
            <Input
              id="filtro-de"
              type="date"
              value={search.de ?? ''}
              onChange={(e) => {
                filtrar({ de: e.target.value === '' ? undefined : e.target.value })
              }}
            />
          </FiltroCampo>
          <FiltroCampo id="filtro-ate" label={t('billing:filters.to')}>
            <Input
              id="filtro-ate"
              type="date"
              value={search.ate ?? ''}
              onChange={(e) => {
                filtrar({ ate: e.target.value === '' ? undefined : e.target.value })
              }}
            />
          </FiltroCampo>
          <FiltroCampo id="filtro-status" label={t('billing:filters.status')} className="w-40">
            <SelectNative
              id="filtro-status"
              value={search.status ?? ''}
              onChange={(e) => {
                filtrar({
                  status: e.target.value === '' ? undefined : (e.target.value as 'open' | 'paid'),
                })
              }}
            >
              <option value="">{t('billing:filters.allStatuses')}</option>
              <option value="open">{t('billing:scheduleStatus.open')}</option>
              <option value="paid">{t('billing:scheduleStatus.paid')}</option>
            </SelectNative>
          </FiltroCampo>
        </TableToolbar>

        <DataTable
          columns={columns}
          page={data}
          isLoading={isLoading}
          isFetching={isFetching}
          error={error}
          onRetry={() => void refetch()}
          getRowId={(r) => r.id}
          sorting={toSortingState(search.ordenar)}
          onSortingChange={(s) => {
            filtrar({ ordenar: fromSortingState(s, COBRANCAS_ORDEM_DEFAULT) })
          }}
          footer={
            <TablePager
              total={data?.total ?? 0}
              pagina={search.pagina}
              linhas={search.linhas}
              onPageChange={(pagina) => {
                aplicar({ pagina })
              }}
              onLinhasChange={(linhas) => {
                aplicar({ linhas, pagina: 1 })
              }}
            />
          }
          ariaLabel={t('billing:charge.ariaTable')}
          empty={
            chips.length > 0 ? (
              <NoResultsState onLimpar={limparFiltros} />
            ) : (
              <EmptyState
                icon={BellRing}
                title={t('billing:charge.empty.title')}
                description={t('billing:charge.empty.description')}
              />
            )
          }
        />

        {/* Barra de ações em lote (Nibo): aparece com a seleção. */}
        {selecionadosBills.length > 0 && (
          <div className="flex flex-col gap-2">
            <p className="text-ink-muted text-xs" aria-live="polite">
              {t('common:pagination.selected', { count: selecionadosBills.length })}
            </p>
            <CobrancaBulkBar
              selectedBills={selecionadosBills}
              onSettled={() => {
                setSelecionados(new Set())
              }}
            />
          </div>
        )}
      </div>

      <NovaCobrancaDrawer
        key={novaAberta ? 'aberta' : 'fechada'}
        open={novaAberta}
        onOpenChange={setNovaAberta}
      />
    </>
  )
}

/** Checkbox "selecionar todos" — `indeterminate` só existe via DOM, daí o ref. */
function SelectAllCheckbox({
  checked,
  indeterminate,
  disabled,
  onChange,
  ...aria
}: {
  checked: boolean
  indeterminate: boolean
  disabled: boolean
  onChange: () => void
  'aria-label': string
}) {
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (ref.current !== null) ref.current.indeterminate = indeterminate
  }, [indeterminate])
  return (
    <input
      ref={ref}
      type="checkbox"
      className="accent-action size-3.5"
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      {...aria}
    />
  )
}

/**
 * Cobrar é escolher um agendamento A RECEBER em aberto. Não há campos livres:
 * valor, vencimento e contato vêm do próprio agendamento.
 */
function NovaCobrancaDrawer({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t, i18n } = useTranslation(['billing', 'common'])
  const qc = useQueryClient()
  const mensagemDeErro = useApiErrorMessage()
  const { key: idempotencyKey, renew } = useIdempotencyKey()
  const [scheduleId, setScheduleId] = useState('')

  const abertos = useQuery(schedulesQueryOptions({ kind: 'receivable', status: 'open' }))
  const lista = abertos.data?.items ?? []

  const criar = useMutation({
    mutationFn: () => createCharge(scheduleId, idempotencyKey),
    onSuccess: async (res) => {
      await qc.invalidateQueries({ queryKey: chargeKeys.lists() })
      toast.success(res.created ? t('billing:charge.created') : t('billing:charge.alreadyCreated'))
      renew()
      onOpenChange(false)
    },
    onError: (err: unknown) => {
      toast.error(mensagemDeErro(err))
    },
  })

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title={t('billing:actions.newInvoice')}
      footer={
        <>
          <Button
            variant="primary"
            disabled={criar.isPending || scheduleId === ''}
            onClick={() => {
              criar.mutate()
            }}
          >
            {criar.isPending ? t('common:states.loading') : t('billing:charge.submit')}
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              onOpenChange(false)
            }}
          >
            {t('common:actions.cancel')}
          </Button>
        </>
      }
    >
      {/* A tela é `partial` no nav: registra a cobrança, mas NÃO emite boleto
          nem envia nada — isso é integração bancária futura. */}
      <p
        role="status"
        className="border-caution-bg bg-caution-bg text-caution mb-4 rounded-md border px-3 py-2 text-sm"
      >
        {t('billing:charge.partialNote')}
      </p>

      <div className="flex flex-col gap-1">
        <Label htmlFor="scheduleId">{t('billing:charge.schedule')}</Label>
        <SelectNative
          id="scheduleId"
          value={scheduleId}
          onChange={(e) => {
            setScheduleId(e.target.value)
          }}
        >
          <option value="">{t('billing:charge.schedulePlaceholder')}</option>
          {lista.map((s) => (
            <option key={s.id} value={s.id}>
              {s.stakeholder.name} · {formatBusinessDate(s.due_date, i18n.language)} · {s.amount}
            </option>
          ))}
        </SelectNative>
        {lista.length === 0 && !abertos.isLoading && (
          <span className="text-ink-muted text-2xs">{t('billing:charge.noOpenSchedules')}</span>
        )}
      </div>
    </Drawer>
  )
}
